import {GUILD_API_ENDPOINT} from '../constants/backendUrls'
import {axiosBaseQuery} from '../rtkQueryUtils'
import {IdType} from '../util/models/IdType'
import {ItemList, ItemListType} from './model/ItemList'
import {UpdatedItemList} from './model/UpdatedItemList'
import {createApi} from '@reduxjs/toolkit/query/react'
import {SEARCH_ITEM_ITEM_LIST_TAG, searchAPI} from '../search/rtkSearchApi'

export const ITEM_LISTS = 'ItemLists'
export const ITEM_LIST = 'ItemList'

export const itemListAPI = createApi({
    reducerPath: 'itemListAPI',
    keepUnusedDataFor: 0,
    tagTypes: [ITEM_LISTS, ITEM_LIST],
    baseQuery: axiosBaseQuery({baseUrl: `${GUILD_API_ENDPOINT}/api/v1/datx/`}),
    endpoints: (builder) => ({
        getItemLists: builder.query<ItemList[], IdType | undefined>({
            query: (guildId) => ({
                url: `guilds/${guildId}/itemlists/me`,
                method: 'GET'
            }),
            providesTags: [ITEM_LISTS, ITEM_LIST]
        }),
        updateItemList: builder.mutation<any, { listId: IdType, data: UpdatedItemList }>({
            query: ({listId, data}) => ({
                url: `itemlists/${listId}`,
                method: 'PATCH',
                data
            }),
            transformErrorResponse: () => 'Something went wrong during your list update',
            invalidatesTags: [ITEM_LISTS, ITEM_LIST]
        }),

        updateItemsOfItemList: builder.mutation<any, { listId: IdType, data: UpdatedItemList }>({
            query: ({listId, data}) => ({
                url: `itemlists/${listId}/items`,
                method: 'PATCH',
                data
            }),
            transformErrorResponse: () => 'Something went wrong during your list update',
            invalidatesTags: [ITEM_LISTS, ITEM_LIST],
            async onQueryStarted(_, {dispatch, queryFulfilled}) {
                await queryFulfilled
                dispatch(searchAPI.util.invalidateTags([SEARCH_ITEM_ITEM_LIST_TAG]))
            }
        }),

        deleteItemsFromItemList: builder.mutation<any, { listId: IdType, data: UpdatedItemList }>({
            query: ({listId, data}) => ({
                url: `itemlists/${listId}/items`,
                method: 'DELETE',
                data
            }),
            transformErrorResponse: () => 'Something went wrong during your list update',
            invalidatesTags: [ITEM_LISTS, ITEM_LIST],
            async onQueryStarted(_, {dispatch, queryFulfilled}) {
                await queryFulfilled
                dispatch(searchAPI.util.invalidateTags([SEARCH_ITEM_ITEM_LIST_TAG]))
            }
        }),
        duplicateItemList: builder.mutation<any, { guildId: IdType, itemListId: IdType }>({
            query: ({guildId, itemListId}) => ({
                url: `guilds/${guildId}/itemlists/${itemListId}/duplicate`,
                method: 'POST'
            }),
            transformErrorResponse: () => 'Something went wrong during your list duplication',
            invalidatesTags: [ITEM_LISTS, ITEM_LIST]
        }),
        deleteItemLists: builder.mutation<any, IdType[]>({
            query: (itemListIds) => ({
                url: 'itemlists',
                method: 'DELETE',
                data: JSON.stringify(itemListIds)
            }),
            transformErrorResponse: () => 'Something went wrong during your list deletion',
            invalidatesTags: [ITEM_LISTS, ITEM_LIST]
        }),
        getPersonalItemLists: builder.query<ItemList[], IdType | undefined>({
            query: (guildId) => ({
                url: `guilds/${guildId}/itemlists/me/personal`,
                method: 'GET'
            })
        }),
        mergeItemLists: builder.mutation<any, { guildId: IdType, itemListIds: IdType[] }>({
            query: ({guildId, itemListIds}) => ({
                url: `guilds/${guildId}/itemlists/merge`,
                method: 'POST',
                data: JSON.stringify(itemListIds)
            }),
            transformErrorResponse: () => 'There is an error merging the list. Please try again.',
            invalidatesTags: [ITEM_LISTS, ITEM_LIST]
        }),
        createItemList: builder.mutation<ItemList, { guildId: IdType, name: string, items?: IdType[], type?: ItemListType }>({
            query: ({guildId, name, items, type}) => ({
                url: 'itemlists',
                method: 'POST',
                data: {
                    guildId: guildId,
                    name: name,
                    items: items,
                    type: type
                }
            }),
            transformErrorResponse: () => 'There is an error creating the list. Please try again.',
            invalidatesTags: [ITEM_LISTS, ITEM_LIST]
        }),
        shareItemList: builder.mutation<any, IdType>({
            query: (itemListId) => ({
                url: `itemlists/${itemListId}/share`,
                method: 'PATCH'
            }),
            transformErrorResponse: () => 'There is an error updating the list. Please try again.',
            invalidatesTags: [ITEM_LISTS, ITEM_LIST]
        }),
        unshareItemList: builder.mutation<any, IdType>({
            query: (itemListId) => ({
                url: `itemlists/${itemListId}/unshare`,
                method: 'PATCH'
            }),
            transformErrorResponse: () => 'There is an error updating the list. Please try again.',
            invalidatesTags: [ITEM_LISTS, ITEM_LIST]
        }),
        getItemList: builder.query<
            ItemList,
            { listId: IdType | undefined }
        >({
            query: ({listId}) => ({
                url: `itemlists/${listId}`,
                method: 'GET'
            }),
            providesTags: [ITEM_LISTS, ITEM_LIST]
        }),
    })
})

export const {
    useGetItemListsQuery,
    useMergeItemListsMutation,
    useGetPersonalItemListsQuery,
    useDuplicateItemListMutation,
    useDeleteItemListsMutation,
    useUpdateItemListMutation,
    useUpdateItemsOfItemListMutation,
    useDeleteItemsFromItemListMutation,
    useCreateItemListMutation,
    useShareItemListMutation,
    useUnshareItemListMutation,
    useGetItemListQuery,
} = itemListAPI
